import { ReactNode } from 'react';

import { useTranslation } from 'react-i18next';

import Whatsapp from '@/components/atoms/Icons/Whatsapp';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { cn } from '@/lib/utils';
import { ResponseMessage } from '@/shared/types/tryIt';

import { responseClasses } from './constants';

type WhatsAppFlowMessage = Extract<ResponseMessage, { type: 'whatsapp_flow' }>;

interface WhatsAppFlowResponseProps {
  response: WhatsAppFlowMessage;
  icon: ReactNode;
  turnRow?: boolean;
}

export const WhatsAppFlowResponse = ({
  response,
  icon,
  turnRow = false,
}: WhatsAppFlowResponseProps) => {
  const { t } = useTranslation();
  const flow = response.whatsapp_flow;

  const clamp = 'self-stretch overflow-hidden text-ellipsis line-clamp-1';

  if (turnRow) {
    return (
      <div className="flex h-60 max-h-60 max-w-xs shrink-0 flex-col items-start gap-4 self-stretch overflow-hidden rounded-xl border border-stroke bg-background px-3 py-4 shadow-sm">
        <div className="flex flex-1 flex-col items-start gap-4 self-stretch">
          <div className="flex flex-col items-start gap-1 self-stretch">
            {flow?.flow_header && (
              <p
                className={cn(
                  clamp,
                  'max-h-14 min-h-6 text-base leading-6 font-semibold text-foreground'
                )}
              >
                {flow.flow_header}
              </p>
            )}
            {flow?.flow_body && (
              <p
                className={cn(
                  clamp,
                  'max-h-15 min-h-5 text-sm leading-5 font-normal text-foreground'
                )}
              >
                {flow.flow_body}
              </p>
            )}
            {flow?.flow_footer && (
              <p
                className={cn(
                  clamp,
                  'max-h-15 min-h-5 text-xs leading-5 font-normal text-foreground-muted'
                )}
              >
                {flow.flow_footer}
              </p>
            )}
          </div>

          {flow?.flow_cta && (
            <button
              type="button"
              disabled
              className={cn(
                'flex min-w-16 cursor-default items-center justify-center gap-0 self-stretch rounded-md',
                'border border-border bg-background px-2 py-1.5',
                'text-center text-sm leading-6 font-medium text-foreground opacity-60'
              )}
            >
              {flow.flow_cta}
            </button>
          )}
        </div>

        <div className="flex flex-col items-start gap-2 self-stretch">
          <Separator />
          <div className="flex items-center gap-1 self-stretch">
            <Whatsapp size={16} />
            <span className={cn(clamp, 'text-xs leading-5 font-normal text-foreground-muted')}>
              {t('conversation.whatsapp_flow_only')}
            </span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={responseClasses}>
      {icon}
      <div className="flex max-w-sm flex-col gap-4 overflow-hidden rounded-xl bg-background p-4 shadow-sm">
        <div className="flex flex-1 flex-col gap-2 text-sm leading-5">
          <div className="flex flex-col gap-1 text-foreground">
            {flow?.flow_header && <p className="font-semibold">{flow.flow_header}</p>}
            {flow?.flow_body && <p>{flow.flow_body}</p>}
          </div>
          {flow?.flow_footer && <p className="text-foreground-muted">{flow.flow_footer}</p>}
        </div>

        {flow?.flow_cta && (
          <Button variant="outline-neutral" size="sm" disabled className="w-full">
            {flow.flow_cta}
          </Button>
        )}

        <div className="flex items-center gap-1 border-t border-stroke pt-2">
          <Whatsapp size={14} />
          <span className="text-xs leading-4 text-foreground-muted">
            {t('conversation.whatsapp_flow_only')}
          </span>
        </div>
      </div>
    </div>
  );
};
